import { computed, ComputedRef } from "vue";
import { storeToRefs } from "pinia";
import { useTasksStore } from "@/stores/v2";
import { Vaccine, VaccineMethod, Tank, VaccineConfiguration, TankGroup, GroupTag } from "@/types/v2";
import { isVaccinationTask } from "@/stores/v2/sites/utils";

export function useVaccination() {
  // ===================================================================
  // Store
  // ===================================================================
  const { activeTask } = storeToRefs(useTasksStore());

  // ===================================================================
  // State
  // ===================================================================
  const configurations: ComputedRef<VaccineConfiguration[]> = computed(() => {
    if (!activeTask.value || !isVaccinationTask(activeTask.value)) return [];
    return activeTask.value.details.configurations || [];
  });

  const vaccines: ComputedRef<Vaccine[]> = computed(() => {
    if (!activeTask.value || !isVaccinationTask(activeTask.value)) return [];
    return activeTask.value.details.vaccines || [];
  });

  const vaccineMethods: ComputedRef<VaccineMethod[]> = computed(() => {
    if (!activeTask.value || !isVaccinationTask(activeTask.value)) return [];
    return activeTask.value.details.vaccineMethods || [];
  });

  const tanks: ComputedRef<Tank[]> = computed(() => {
    if (!activeTask.value || !isVaccinationTask(activeTask.value)) return [];
    return activeTask.value.details.tanks || [];
  });

  const availableTags: ComputedRef<GroupTag[]> = computed(() => {
    if (!activeTask.value) return [];
    return (activeTask.value as any).availableTags || [];
  });

  // ===================================================================
  // Getters
  // ===================================================================
  // Get Groups by Tank key
  const getGroupsOfTank = (key: string): TankGroup[] | [] => {
    if (!tanks.value || !tanks.value.length) return [];

    const tank: Tank | null = tanks.value.find((tank: Tank) => tank.key === key) ?? null;
    if (!tank || !tank.groups) return [];

    return tank.groups;
  }

  return {
    // ===================================================================
    // Getters
    // ===================================================================
    getGroupsOfTank,

    // ===================================================================
    // State
    // ===================================================================
    vaccines,
    vaccineMethods,
    tanks,
    configurations,
    availableTags,
  };
}
